import React from "react";
import { FiCopy } from "react-icons/fi";
import { showPayoutNotification } from "./PayoutNotification";

type PayoutTableCellProps = {
  value: React.ReactNode;
  copyValue?: string;
  title?: string;
  className?: string;
  valueClassName?: string;
  maxWidthClassName?: string;
  copy?: boolean;
  onClick?: () => void;
};

const textValueFromNode = (value: React.ReactNode) => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return "";
};

function PayoutTableCell({
  value,
  copyValue,
  title,
  className = "",
  valueClassName = "",
  maxWidthClassName = "max-w-[220px]",
  copy = true,
  onClick,
}: PayoutTableCellProps) {
  const textValue = copyValue ?? textValueFromNode(value);
  const canCopy = copy && textValue.trim() && textValue !== "-";

  const copyToClipboard = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(textValue);
      showPayoutNotification({
        tone: "success",
        title: "Copied",
        message: "Table value copied to clipboard.",
      });
    } catch {
      showPayoutNotification({
        tone: "error",
        title: "Copy failed",
        message: "Unable to copy this table value.",
      });
    }
  };

  return (
    <span
      className={
        "group/payout-table-cell flex min-h-[40px] min-w-0 items-center gap-2 " +
        maxWidthClassName +
        " " +
        className
      }
      title={title ?? textValue}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={
            "block min-w-0 flex-1 truncate whitespace-nowrap text-left " +
            valueClassName
          }
        >
          {value}
        </button>
      ) : (
        <span
          className={
            "block min-w-0 flex-1 truncate whitespace-nowrap " + valueClassName
          }
        >
          {value}
        </span>
      )}
      {canCopy && (
        <button
          type="button"
          onClick={copyToClipboard}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] border border-[#E0E5F2] bg-white text-[13px] text-[#505E8E] opacity-0 transition hover:text-[#1B163B] focus:opacity-100 group-hover/payout-table-cell:opacity-100"
          aria-label="Copy table value"
          title="Copy"
        >
          <FiCopy />
        </button>
      )}
    </span>
  );
}

export default PayoutTableCell;
